// Compliance Schedule Page - Calendar View
// Shows all scheduled inspections in a calendar format with filtering

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar as CalendarIcon, Search, ChevronLeft, ChevronRight, Clock, MapPin, User, Shield } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isToday } from 'date-fns';
import { cn } from '@/lib/utils';
import { mockAssetComplianceList } from '@/lib/mock-compliance-data';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';

function getPriorityFromStatus(status: string | undefined): 'high' | 'medium' | 'low' {
  if (status === 'overdue') return 'high';
  if (status === 'due-soon') return 'medium';
  return 'low';
}

interface ScheduledInspection {
  id: string;
  assetId: string;
  assetName: string;
  location: string;
  category: string;
  inspector: string;
  scheduledDate: Date;
  priority: 'high' | 'medium' | 'low';
  regime: string;
}

export default function ComplianceSchedulePage(): React.ReactElement {
  const { modules } = useFeatureSettings();
  const complianceModule = modules.compliance;
  const isDemoMode = complianceModule?.mode === 'demo';
  const backendUrl = useGetBackendUrl();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [inspections, setInspections] = useState<ScheduledInspection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterRegulations, setFilterRegulations] = useState<string[]>([]);
  const [dayDetailsOpen, setDayDetailsOpen] = useState(false);

  // Load inspection data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        if (isDemoMode) {
          // Demo mode: use mock data
          await new Promise((resolve) => setTimeout(resolve, 300));
          const assetData = mockAssetComplianceList;
          
          // Convert asset compliance data to scheduled inspections
          const scheduledInspections: ScheduledInspection[] = assetData
            .filter(asset => asset.nextInspectionDate)
            .map(asset => ({
              id: asset.assetId,
              assetId: asset.assetId,
              assetName: asset.assetName,
              location: asset.location || 'Unknown',
              category: asset.category || 'General',
              inspector: 'Inspector ' + Math.floor(Math.random() * 5 + 1),
              scheduledDate: new Date(asset.nextInspectionDate!),
              priority: getPriorityFromStatus(asset.status),
              regime: asset.regimeName,
            }));

          setInspections(scheduledInspections);
        } else {
          // Live mode: fetch from API
          try {
            const response = await fetch(`${backendUrl}/api/Compliance/checks`);
            
            if (response.ok) {
              const data = await response.json();
              console.log('Fetched inspection schedule from API:', data);
              setInspections(data);
            } else {
              console.error('API returned error status:', response.status);
              setInspections([]);
            }
          } catch (apiError) {
            console.error('Failed to load inspection schedule from API:', apiError);
            // In live mode, don't fall back to demo data
            setInspections([]);
          }
        }
      } catch (error) {
        console.error('Failed to load inspection schedule:', error);
        if (!isDemoMode) {
          // In live mode, show empty state on error
          setInspections([]);
        }
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [isDemoMode, backendUrl]);

  // Get unique locations and categories for filters
  const locations = useMemo(() => {
    const uniqueLocations = Array.from(new Set(inspections.map(i => i.location)));
    return uniqueLocations.sort((a, b) => a.localeCompare(b));
  }, [inspections]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(inspections.map(i => i.category)));
    return uniqueCategories.sort((a, b) => a.localeCompare(b));
  }, [inspections]);

  const regulations = useMemo(() => {
    const uniqueRegulations = Array.from(new Set(inspections.map(i => i.regime)));
    return uniqueRegulations.sort((a, b) => a.localeCompare(b));
  }, [inspections]);

  // Filter inspections based on search and filters
  const filteredInspections = useMemo(() => {
    return inspections.filter(inspection => {
      const matchesSearch = searchQuery === '' || 
        inspection.assetName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inspection.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inspection.inspector.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesLocation = filterLocation === 'all' || inspection.location === filterLocation;
      const matchesCategory = filterCategory === 'all' || inspection.category === filterCategory;
      const matchesPriority = filterPriority === 'all' || inspection.priority === filterPriority;
      const matchesRegulation = filterRegulations.length === 0 || filterRegulations.includes(inspection.regime);

      return matchesSearch && matchesLocation && matchesCategory && matchesPriority && matchesRegulation;
    });
  }, [inspections, searchQuery, filterLocation, filterCategory, filterPriority, filterRegulations]);

  // Get calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Get inspections for a specific day
  const getInspectionsForDay = (day: Date) => {
    return filteredInspections.filter(inspection => 
      isSameDay(inspection.scheduledDate, day)
    );
  };

  // Get inspections for selected date
  const selectedDateInspections = useMemo(() => {
    if (!selectedDate) return [];
    return getInspectionsForDay(selectedDate);
  }, [selectedDate, filteredInspections]);

  // Navigate months
  const previousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Handle day click
  const handleDayClick = (day: Date) => {
    const dayInspections = getInspectionsForDay(day);
    if (dayInspections.length > 0) {
      setSelectedDate(day);
      setDayDetailsOpen(true);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityBadgeVariant = (priority: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Page Header */}
      <div className="bg-card border-b">
        <div className="p-6">
          <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Compliance Management
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Physical asset compliance management for UK HSE & British Standards
              </p>
            </div>
          </div>
        </div>
      </div>



      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by asset, location, or inspector..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map(location => (
              <SelectItem key={location} value={location}>{location}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(category => (
              <SelectItem key={category} value={category}>{category}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <div className="w-[180px]">
          <MultiSelect
            options={regulations.map(reg => ({ value: reg, label: reg }))}
            placeholder="Regulations/Rules"
            emptyMessage="No regulations found"
            value={filterRegulations}
            onValueChange={setFilterRegulations}
            maxDisplay={2}
          />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* Calendar */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">
                {format(currentDate, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={previousMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={nextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <CardDescription>
              {filteredInspections.length} inspection{filteredInspections.length !== 1 ? 's' : ''} scheduled
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground p-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((day, index) => {
                const dayInspections = getInspectionsForDay(day);
                const hasInspections = dayInspections.length > 0;
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isTodayDate = isToday(day);
                const showAll = dayInspections.length <= 3;
                const displayInspections = showAll ? dayInspections : dayInspections.slice(0, 2);
                const remainingCount = dayInspections.length - displayInspections.length;

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => handleDayClick(day)}
                    disabled={!hasInspections}
                    className={cn(
                      "min-h-[100px] p-2 rounded-lg border transition-colors text-left relative",
                      isCurrentMonth ? "bg-background" : "bg-muted/30",
                      isTodayDate && "ring-2 ring-primary",
                      hasInspections && "hover:bg-accent cursor-pointer",
                      !hasInspections && "cursor-default"
                    )}
                  >
                    <div className={cn(
                      "text-sm font-medium mb-1",
                      !isCurrentMonth && "text-muted-foreground",
                      isTodayDate && "text-primary font-bold"
                    )}>
                      {format(day, 'd')}
                    </div>
                    
                    {hasInspections && (
                      <div className="space-y-1">
                        {displayInspections.map((inspection) => (
                          <div
                            key={inspection.id}
                            className={cn(
                              "text-xs px-1.5 py-0.5 rounded truncate",
                              getPriorityColor(inspection.priority),
                              "text-white"
                            )}
                            title={inspection.assetName}
                          >
                            {inspection.assetName}
                          </div>
                        ))}
                        {remainingCount > 0 && (
                          <div className="text-xs text-muted-foreground font-medium">
                            +{remainingCount} more
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-red-500" />
                <span className="text-sm text-muted-foreground">High Priority</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-orange-500" />
                <span className="text-sm text-muted-foreground">Medium Priority</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-green-500" />
                <span className="text-sm text-muted-foreground">Low Priority</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Inspections List */}
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Inspections</CardTitle>
            <CardDescription>
              Next 30 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <ScrollAreaViewport>
                {filteredInspections.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CalendarIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground">No inspections scheduled</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredInspections
                      .sort((a, b) => {
                        const aTime = a.scheduledDate?.getTime() ?? 0;
                        const bTime = b.scheduledDate?.getTime() ?? 0;
                        return aTime - bTime;
                      })
                      .slice(0, 50)
                      .map((inspection) => (
                        <Card key={inspection.id} className="p-4">
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{inspection.assetName}</h4>
                                <p className="text-sm text-muted-foreground">{inspection.regime}</p>
                              </div>
                              <Badge variant={getPriorityBadgeVariant(inspection.priority)}>
                                {inspection.priority}
                              </Badge>
                            </div>
                            
                            <div className="space-y-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Clock className="h-3.5 w-3.5" />
                                <span>{format(inspection.scheduledDate, 'MMM d, yyyy')}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5" />
                                <span>{inspection.location}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <User className="h-3.5 w-3.5" />
                                <span>{inspection.inspector}</span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                  </div>
                )}
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Day Details Modal */}
      <Dialog open={dayDetailsOpen} onOpenChange={setDayDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Inspections for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </DialogTitle>
            <DialogDescription>
              {selectedDateInspections.length} inspection{selectedDateInspections.length !== 1 ? 's' : ''} scheduled
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            <ScrollAreaViewport>
              <div className="space-y-4 pr-4">
                {selectedDateInspections.map((inspection) => (
                  <Card key={inspection.id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <h4 className="font-semibold">{inspection.assetName}</h4>
                          <p className="text-sm text-muted-foreground">{inspection.regime}</p>
                        </div>
                        <Badge variant={getPriorityBadgeVariant(inspection.priority)}>
                          {inspection.priority}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Location:</span>
                          <p className="font-medium">{inspection.location}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Category:</span>
                          <p className="font-medium">{inspection.category}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Inspector:</span>
                          <p className="font-medium">{inspection.inspector}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Asset ID:</span>
                          <p className="font-medium">{inspection.assetId}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </DialogContent>
      </Dialog>
        </div>
      </div>
    </div>
  );
}
